/**
 * Clinical → eTMF completeness — the per-document AI agent (ADR-0036).
 *
 * Every call names a Veeva connection (or a trial on one) the caller may
 * `use`; the backend checks it on every request (no Administrator bypass).
 * Saving the field mapping is Developer+. A check runs as a `completeness`
 * solution run in the background; poll it with `getRun` (your own run) or by
 * re-reading the trial.
 */

import type { components } from "./_generated/schema";
import { apiFetch } from "./apiFetch";

export type EtmfConnectionOut = components["schemas"]["EtmfConnectionOut"];
export type EtmfConnectionListOut = components["schemas"]["EtmfConnectionListOut"];
export type EtmfMappingOut = components["schemas"]["MappingOut"];
export type EtmfMappingIn = components["schemas"]["MappingIn"];
export type StudyRecordOut = components["schemas"]["StudyRecordOut"];
export type ReferenceModelOut = components["schemas"]["ReferenceModelOut"];
export type ArtifactOut = components["schemas"]["ArtifactOut"];
export type TrialOut = components["schemas"]["TrialOut"];
export type TrialDetailOut = components["schemas"]["TrialDetailOut"];
export type TrialSourceOut = components["schemas"]["SourceOut"];
export type TrialSummary = components["schemas"]["TrialSummary"];
export type CompletenessResult = components["schemas"]["CompletenessResult"];
export type CompletenessRow = components["schemas"]["CompletenessRow"];
export type ColumnScore = components["schemas"]["ColumnScore"];
export type MetadataCheck = components["schemas"]["MetadataCheck"];
export type MetadataInconsistency = components["schemas"]["MetadataInconsistency"];
export type MetadataComparison = components["schemas"]["MetadataComparison"];
export type UnmatchedDocument = components["schemas"]["UnmatchedDocument"];
export type CreateTrialIn = components["schemas"]["CreateTrialIn"];
export type StartCheckIn = components["schemas"]["StartCheckIn"];
export type StartCheckOut = components["schemas"]["StartCheckOut"];

const BASE = "/api/v1/etmf-completeness";
const conn = (id: string) => `${BASE}/connections/${encodeURIComponent(id)}`;
const trial = (id: string) => `${BASE}/trials/${encodeURIComponent(id)}`;

export function listEtmfConnections(token: string): Promise<EtmfConnectionListOut> {
  return apiFetch<EtmfConnectionListOut>(`${BASE}/connections`, { token });
}

export function getEtmfMapping(token: string, connectionId: string): Promise<EtmfMappingOut> {
  return apiFetch<EtmfMappingOut>(`${conn(connectionId)}/mapping`, { token });
}

export function saveEtmfMapping(
  token: string,
  connectionId: string,
  body: EtmfMappingIn,
): Promise<EtmfMappingOut> {
  return apiFetch<EtmfMappingOut>(`${conn(connectionId)}/mapping`, {
    method: "PUT",
    token,
    json: body,
  });
}

export function listStudyRecords(
  token: string,
  connectionId: string,
  q?: string,
): Promise<{ items: StudyRecordOut[] }> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  return apiFetch<{ items: StudyRecordOut[] }>(`${conn(connectionId)}/study-records${qs}`, {
    token,
  });
}

export function getReferenceModel(token: string): Promise<ReferenceModelOut> {
  return apiFetch<ReferenceModelOut>(`${BASE}/reference-model`, { token });
}

export function listTrials(token: string): Promise<{ items: TrialOut[] }> {
  return apiFetch<{ items: TrialOut[] }>(`${BASE}/trials`, { token });
}

export function createTrial(token: string, body: CreateTrialIn): Promise<TrialDetailOut> {
  return apiFetch<TrialDetailOut>(`${BASE}/trials`, { method: "POST", token, json: body });
}

export function getTrial(token: string, trialId: string): Promise<TrialDetailOut> {
  return apiFetch<TrialDetailOut>(trial(trialId), { token });
}

export function uploadTrialSource(
  token: string,
  trialId: string,
  role: "protocol" | "sop",
  file: File,
): Promise<TrialDetailOut> {
  const form = new FormData();
  form.set("role", role);
  form.set("file", file);
  return apiFetch<TrialDetailOut>(`${trial(trialId)}/sources`, {
    method: "POST",
    token,
    body: form,
  });
}

export function removeTrialSource(
  token: string,
  trialId: string,
  sourceId: string,
  reason: string,
): Promise<TrialDetailOut> {
  return apiFetch<TrialDetailOut>(
    `${trial(trialId)}/sources/${encodeURIComponent(sourceId)}?reason=${encodeURIComponent(reason)}`,
    { method: "DELETE", token },
  );
}

export function startTrialCheck(
  token: string,
  trialId: string,
  body: StartCheckIn,
): Promise<StartCheckOut> {
  return apiFetch<StartCheckOut>(`${trial(trialId)}/checks`, { method: "POST", token, json: body });
}

/** A Vault document's page, for "Open in Vault". */
export function vaultDocumentUrl(vaultUrl: string | null | undefined, documentId: string) {
  return vaultUrl ? `${vaultUrl}/ui/#doc_info/${encodeURIComponent(documentId)}` : null;
}

// ---------------------------------------------------------------------------
// Row reviews (ADR-0043): assign a trial row to a user; the assignee reviews
// it under Validation → My Tests.
// ---------------------------------------------------------------------------

export type AssignableUserOut = components["schemas"]["AssignableUserOut"];
export type AssignmentOut = components["schemas"]["AssignmentOut"];
export type MyReviewOut = components["schemas"]["MyReviewOut"];

export function listAssignableUsers(token: string): Promise<{ items: AssignableUserOut[] }> {
  return apiFetch<{ items: AssignableUserOut[] }>(`${BASE}/assignable-users`, { token });
}

export function listTrialAssignments(
  token: string,
  trialId: string,
): Promise<{ items: AssignmentOut[] }> {
  return apiFetch<{ items: AssignmentOut[] }>(`${trial(trialId)}/assignments`, { token });
}

export function assignTrialRow(
  token: string,
  trialId: string,
  artifactNumber: string,
  assigneeUserId: string | null,
): Promise<{ items: AssignmentOut[] }> {
  return apiFetch<{ items: AssignmentOut[] }>(
    `${trial(trialId)}/assignments/${encodeURIComponent(artifactNumber)}`,
    { method: "PUT", token, json: { assignee_user_id: assigneeUserId } },
  );
}

export function listMyReviews(token: string): Promise<{ items: MyReviewOut[] }> {
  return apiFetch<{ items: MyReviewOut[] }>(`${BASE}/my-reviews`, { token });
}

export function completeMyReview(
  token: string,
  reviewId: string,
  note: string | null,
): Promise<MyReviewOut> {
  return apiFetch<MyReviewOut>(`${BASE}/my-reviews/${encodeURIComponent(reviewId)}/complete`, {
    method: "POST",
    token,
    json: { note },
  });
}

/** The row's Vault document (PDF rendition when Vault has one). */
export async function fetchReviewDocument(token: string, reviewId: string): Promise<Blob> {
  const response = await fetch(`${BASE}/my-reviews/${encodeURIComponent(reviewId)}/document`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Could not open the document (HTTP ${response.status})`);
  return response.blob();
}
